import { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
  HealthProgramStatisticsResponse,
  HealthProgramTrainingCompletion,
  ITrainingFields,
} from '@estercare/ester-shared'
import { theme } from '../../../../theme/theme'
import { ChartCard } from '../../../charts/ChartCard'
import { CompletionBar } from '../../../charts/CompletionBar'
import { useGetEntriesByContentType } from '../../../../api/contentful/contentfulQueries'
import {
  getCompetenceCompletionSteps,
  getCompletionValues,
  getEligibleGroupKey,
  getFallbackNameKey,
} from '../../../../utils/statistics/healthProgram'
import { StatisticsSection } from '../StatisticsSection'

type Props = {
  statistics?: HealthProgramStatisticsResponse
}

export const CompetenceSection = ({ statistics }: Props) => {
  const { t } = useTranslation()
  const { data: trainingEntries } = useGetEntriesByContentType<ITrainingFields>('training')

  const completionSteps = useMemo(() => getCompetenceCompletionSteps(t), [t])

  // No response means the request failed, not a thin training set - the view reports that itself.
  if (!statistics) return null

  const { trainings } = statistics

  const trainingTitle = ({ trainingId }: HealthProgramTrainingCompletion) => {
    const entry = trainingEntries?.items.find(({ fields }) => fields.trainingIdentifier === trainingId)
    const fallbackKey = getFallbackNameKey(trainingId)
    return entry?.fields.title ?? (fallbackKey ? t(fallbackKey) : trainingId)
  }

  const eligibleGroupLabel = ({ trainingId, eligible }: HealthProgramTrainingCompletion) => {
    const key = getEligibleGroupKey(trainingId)
    return key ? t(key, { count: eligible }) : undefined
  }

  return (
    <StatisticsSection title={t('admin.statistics.competenceTitle')} intro={t('admin.statistics.competenceIntro')}>
      <TrainingGrid>
        {trainings.map((training) => (
          <ChartCard key={training.trainingId} title={trainingTitle(training)} caption={eligibleGroupLabel(training)}>
            <CompletionBar
              steps={completionSteps}
              values={getCompletionValues(training)}
              unit={t('admin.statistics.completionUnit')}
            />
          </ChartCard>
        ))}
      </TrainingGrid>
    </StatisticsSection>
  )
}

const TrainingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.large}px;
  margin-top: ${theme.spacing.large}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.small}px;
  }
`
